import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { SectorGeoJsonProperties } from '@/types/sector';

interface SectorPolygonsLayerProps {
  geojson: GeoJSON.FeatureCollection | undefined;
  selectedSectorIds?: Set<string>;
  onSectorClick?: (sectorId: string) => void;
  showLabels?: boolean;
  interactive?: boolean;
}

const DEFAULT_STYLE: L.PathOptions = {
  weight: 2,
  opacity: 0.6,
  fillOpacity: 0.15,
  lineCap: 'round',
  lineJoin: 'round',
  dashArray: undefined,
};

const HOVER_STYLE: Partial<L.PathOptions> = {
  weight: 3,
  opacity: 0.8,
  fillOpacity: 0.25,
};

const SELECTED_STYLE: Partial<L.PathOptions> = {
  weight: 3,
  opacity: 1.0,
  fillOpacity: 0.30,
};

const MUTED_STYLE: Partial<L.PathOptions> = {
  weight: 1,
  opacity: 0.15,
  fillOpacity: 0.03,
};

const SectorPolygonsLayer: React.FC<SectorPolygonsLayerProps> = ({
  geojson,
  selectedSectorIds,
  onSectorClick,
  showLabels = true,
  interactive = true,
}) => {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const labelsRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!geojson || geojson.features.length === 0) return;

    const hasSelection = selectedSectorIds && selectedSectorIds.size > 0;

    const geoJsonLayer = L.geoJSON(geojson, {
      style: (feature) => {
        const props = feature?.properties as SectorGeoJsonProperties;
        const color = props.color || '#5e81f4';
        const isSelected = hasSelection && selectedSectorIds.has(props.id);
        const isMuted = hasSelection && !isSelected;

        return {
          ...DEFAULT_STYLE,
          color,
          fillColor: color,
          ...(isSelected ? SELECTED_STYLE : isMuted ? MUTED_STYLE : {}),
        };
      },
      onEachFeature: interactive
        ? (feature, layer) => {
            const props = feature.properties as SectorGeoJsonProperties;

            layer.on('mouseover', () => {
              const path = layer as L.Path;
              const isSelected = hasSelection && selectedSectorIds?.has(props.id);
              if (!isSelected) {
                path.setStyle({ ...HOVER_STYLE });
              }
            });

            layer.on('mouseout', () => {
              geoJsonLayer.resetStyle(layer);
            });

            layer.on('click', () => {
              onSectorClick?.(props.id);
            });
          }
        : undefined,
    });

    geoJsonLayer.addTo(map);
    layerRef.current = geoJsonLayer;

    // Centroid labels
    const labelGroup = L.layerGroup();
    if (showLabels) {
      geojson.features.forEach((feature) => {
        const props = feature.properties as SectorGeoJsonProperties;
        if (!feature.geometry || feature.geometry.type !== 'Polygon') return;

        // Compute centroid from polygon coordinates
        const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0];
        if (!coords || coords.length === 0) return;

        let sumLat = 0;
        let sumLng = 0;
        const n = coords.length - 1; // Exclude closing point
        for (let i = 0; i < n; i++) {
          const pt = coords[i];
          if (!pt) continue;
          sumLng += pt[0]!;
          sumLat += pt[1]!;
        }
        const centroid: L.LatLngExpression = [sumLat / n, sumLng / n];

        const color = props.color || '#5e81f4';
        const label = L.tooltip({
          permanent: true,
          direction: 'center',
          className: 'sector-chip',
          interactive: false,
        })
          .setLatLng(centroid)
          .setContent(
            `<span class="sector-chip__inner" style="background:${color};border-color:${color}">${props.name}</span>`,
          );

        labelGroup.addLayer(label);
      });
      labelGroup.addTo(map);
    }
    labelsRef.current = labelGroup;

    return () => {
      map.removeLayer(geoJsonLayer);
      map.removeLayer(labelGroup);
      layerRef.current = null;
      labelsRef.current = null;
    };
  }, [map, geojson, selectedSectorIds, onSectorClick, showLabels, interactive]);

  return null;
};

export default SectorPolygonsLayer;
